import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '@/shared/api/endpoints'
import { ApiError, NetworkError } from '@/shared/api/http'
import { FINAL_INVOICE_STATUSES, type PaymentPageResponse } from '@/shared/api/types'

/**
 * Опрос страницы оплаты.
 *
 * Почему опрос, а не «загрузили один раз». Платёж приходит извне и в
 * произвольный момент; страница обязана сама заметить его появление
 * и рост подтверждений. Покупатель, который не видит реакции на свой
 * перевод, платит второй раз — это не редкость, а типичное поведение.
 *
 * Три правила, каждое из которых оплачено чужой болью:
 *
 * 1. Опрос прекращается на конечном статусе. Иначе тысяча забытых
 *    вкладок с оплаченными заявками будет долбить API вечно.
 * 2. Опрос останавливается, когда вкладка скрыта, и делает
 *    НЕМЕДЛЕННЫЙ запрос при возвращении. Покупатель уходит в кошелёк,
 *    платит и возвращается — ровно в этот момент ему нужен свежий
 *    статус, а не через полный интервал ожидания.
 * 3. При отказе интервал растёт. Если бекенд лёг, сотня открытых
 *    страниц не должна добивать его ретраями каждые пять секунд.
 */

const BASE_INTERVAL_MS = 5_000
const MAX_INTERVAL_MS = 60_000

interface State {
  page: PaymentPageResponse | null
  /** Первая загрузка: показываем скелет, а не пустую страницу. */
  loading: boolean
  /** Заявки нет — 404. Отдельно от прочих ошибок: это тупик. */
  notFound: boolean
  /** Последний отказ. Страница при этом продолжает показывать данные. */
  error: Error | null
}

export function usePaymentPage(invoiceId: string) {
  const [state, setState] = useState<State>({
    page: null,
    loading: true,
    notFound: false,
    error: null,
  })

  // Хранится в ref, а не в state: изменение интервала не должно
  // перерисовывать страницу.
  const failuresRef = useRef(0)
  const timerRef = useRef<number | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  // Свежий статус для планировщика. Из state его читать нельзя:
  // замыкание таймера помнит значение на момент своего создания.
  const statusRef = useRef<PaymentPageResponse['status'] | null>(null)

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const fetchOnce = useCallback(async () => {
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    try {
      const page = await api.payments.page(invoiceId, controller.signal)
      failuresRef.current = 0
      statusRef.current = page.status
      setState({ page, loading: false, notFound: false, error: null })
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return

      if (error instanceof ApiError && error.status === 404) {
        statusRef.current = null
        setState({ page: null, loading: false, notFound: true, error: null })
        return
      }

      failuresRef.current += 1
      // Данные не выбрасываем: адрес и сумма на экране остаются
      // верными, даже когда сервер временно недоступен. Покупатель,
      // у которого страница обнулилась в момент перевода, потерян.
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error : new NetworkError(error),
      }))
    }
  }, [invoiceId])

  // Планировщик. Отдельным эффектом, чтобы перезапуск опроса не тянул
  // за собой перезапуск самого запроса.
  useEffect(() => {
    let stopped = false

    const schedule = () => {
      clearTimer()
      if (stopped) return
      if (document.hidden) return
      const status = statusRef.current
      if (status !== null && FINAL_INVOICE_STATUSES.has(status)) return

      const backoff = Math.min(
        BASE_INTERVAL_MS * 2 ** failuresRef.current,
        MAX_INTERVAL_MS,
      )
      timerRef.current = window.setTimeout(() => {
        void fetchOnce().finally(schedule)
      }, backoff)
    }

    const onVisibility = () => {
      if (document.hidden) {
        clearTimer()
        return
      }
      // Вернулись во вкладку — спрашиваем сразу, не дожидаясь интервала.
      void fetchOnce().finally(schedule)
    }

    document.addEventListener('visibilitychange', onVisibility)
    void fetchOnce().finally(schedule)

    return () => {
      stopped = true
      clearTimer()
      abortRef.current?.abort()
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [fetchOnce, clearTimer])

  /** Принудительное обновление: после выбора валюты и по кнопке. */
  const refresh = useCallback(() => {
    void fetchOnce()
  }, [fetchOnce])

  /** Подставить страницу, полученную из другого запроса (выбор валюты). */
  const replace = useCallback((page: PaymentPageResponse) => {
    statusRef.current = page.status
    failuresRef.current = 0
    setState({ page, loading: false, notFound: false, error: null })
  }, [])

  return { ...state, refresh, replace }
}
